import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectItem} from 'helpers/hooks/utils';
import {useTable} from 'components/organisms/Providers/TableProvider/TableProvider';
import constants from 'helpers/constants';
import utils from 'helpers/utils';
import {useAuthClient, useAuthorize} from 'components/organisms/Providers/AuthProvider/AuthProvider';
import StyledEntitiesAssistant from 'components/organisms/Assistants/EntitiesAssistant/EntitiesAssistant.styles';
import {useCollectionGet} from 'services/collection/collection.hooks';
import {useAnalyseAdvice} from 'services/analyse/analyse.hooks';
import {useSourceCreate} from 'services/source/source.utils';
import {useCollectionTagGroupAdd} from 'services/collection/tagGroup/tagGroup.hooks';
import {useCollectionTagGroupPatch} from 'services/collection/tagGroup/tagGroup.utils';
import {useClientCustomFieldAdd} from 'services/client/customField/customField.hooks';
import {useCollectionCustomFieldAdd} from 'services/collection/customField/customField.hooks';
import {useCollectionPatch} from 'services/collection/collection.utils';
import {useDialogControl} from 'components/organisms/Providers/DialogProvider/DialogProvider';
import ConfirmDialog from 'components/organisms/Dialogs/ConfirmDialog/ConfirmDialog';

const EntitiesAssistant = React.forwardRef((props, ref) => {
  const innerProps = useComponentProps(props, 'EntitiesAssistant');

  const tableProvider = useTable();
  const fieldData = tableProvider.fieldData;
  const context = tableProvider.context?.data;
  const isLoading = tableProvider.isLoading() || !utils.isDefined(tableProvider.list?.meta?.resultsCount);

  // special listState without assistant filtering
  const listState = useEffectItem(tableProvider.appliedListState(null, true));

  const client = useAuthClient();
  const universeCollection = useCollectionGet({collectionId: client?.universeCollectionId},
    {enabled: Boolean(client?.universeCollectionId)})?.data;
  const isUniverse = Boolean(context?.universeCollectionId === universeCollection?.collectionId);
  const collection = isUniverse ? universeCollection : context;

  const authorize = useAuthorize();
  const dialogControl = useDialogControl();

  const analyseAdvice = useAnalyseAdvice();

  const createSource = useSourceCreate();
  const createTagGroup = useCollectionTagGroupAdd();
  const updateTagGroup = useCollectionTagGroupPatch();
  const createClientField = useClientCustomFieldAdd();
  const createCollectionField = useCollectionCustomFieldAdd();
  const patchCollection = useCollectionPatch();

  const hasFileUpload = Boolean((client?.props?.fileUploadPeriod ?? constants.services.periods.never) !== constants.services.periods.never) ||
    Boolean((collection?.fileUploadPeriod ?? constants.services.periods.never) !== constants.services.periods.never);
  const hasAutoLookup = Boolean((client?.props?.autoLookupPeriod ?? constants.services.periods.never) !== constants.services.periods.never) ||
    Boolean((collection?.autoLookupPeriod ?? constants.services.periods.never) !== constants.services.periods.never);
  const hasAutoTags = Boolean((client?.props?.autotagPeriod ?? constants.services.periods.never) !== constants.services.periods.never) ||
    Boolean((collection?.autotagPeriod ?? constants.services.periods.never) !== constants.services.periods.never);

  const types = useMemo(() => {
    return constants.data.assistantTypes
      .filter((ea) => {
        return ea.enabled === true || (
          ea.enabled?.length > 0 && (
            (isUniverse && ea.enabled.includes('dealflow')) ||
            (!isUniverse && utils.isDefined(collection) && ea.enabled.includes('collection')) ||
            (!isUniverse && !utils.isDefined(collection) && ea.enabled.includes('database'))
          )
        )
      })
      .filter((ea) => authorize({attribute: `entity.assistant.${ea.value}`}));
  }, [authorize, collection, isUniverse]);

  const handleClose = () => {
    tableProvider.closeAssistant();
  }

  const handleClear = () => {
    tableProvider.assistantState?.clear();
  }

  const handleSwitch = (type) => {
    tableProvider.assistantState?.setAssistantType(type);
  }

  const handleChange = (conversation) => {
    tableProvider.assistantState?.setAssistantConversation(conversation);
  }

  const handleAdvice = (conversation) => {
    return analyseAdvice.mutation.mutateAsync({
      collectionId: collection?.collectionId,
      conversation: conversation,
      allowedTypes: utils.uniqueArray(types.map((t) => t.value).concat([tableProvider.assistantState.assistantType]).filter((_) => (_))),
      filters: {
        ...utils.filter2Object(listState.filter),
        search: listState.search
      },
      count: tableProvider?.list?.meta?.resultsCount
    })
      .then(({response}) => {
        const conversation = utils.camelcase(utils.clone(response.data?.data, true));

        const userMessages = conversation.filter((c) => c.role === constants.assistant.conversation.roles.user);
        const activeMessage = userMessages[userMessages.length - 1];
        const isSearchAssistant = activeMessage.types?.includes(constants.assistant.types.search) &&
          !activeMessage?.types?.includes(constants.assistant.types.setup);

        if ((activeMessage?.progress?.total ?? 0) > 0) {
          if (isSearchAssistant) {
            // cleanup older results and auto process more
            userMessages.forEach((c, idx) => {
              if (idx !== userMessages.length - 1) {
                delete c.results;
              }
            });
          } else {
            // add tag colors and valid count
            if (activeMessage.results?.actions?.length > 0) {
              activeMessage.results.actions = activeMessage.results.actions
                .map((action, actionIdx) => {
                  const result = {
                    ...action,
                    actionId: `${activeMessage.messageId}_${action.id}_${actionIdx}`
                  };

                  if (action?.id === constants.assistant.adviceActionTypes.addTagGroup) {
                    const tg = fieldData.tagGroups.find((tg) => +tg.groupId === +result.details.groupId);
                    if (!tg) {
                      result.details = {
                        ...result.details,
                        color: innerProps.theme.pickAColor('tagGroup')
                      }
                    }
                  }

                  return result;
                });
            }

            // cleanup older results
            if ((activeMessage?.progress?.total ?? 0) > 0) {
              userMessages.forEach((c, idx) => {
                if (idx !== userMessages.length - 1) {
                  delete c.results;
                }
              });
            }
          }
        } else {
          // just message type validation
        }

        return conversation;
      });
  }

  const handleAction = (action, answer, skipped) => {
    const applyAction = () => {
      if (skipped || [
        constants.assistant.adviceActionTypes.askQuestion,
        constants.assistant.adviceActionTypes.needMoreInfo
      ].includes(action.id)) {
        return Promise.resolve('Action applied');
      } else if (action.id === constants.assistant.adviceActionTypes.addScope) {
        return patchCollection(collection, action.details);
      } else if (action.id === constants.assistant.adviceActionTypes.addSource) {
        const addSource = (source) => {
          const isOwned = +source?.ownerClientId === client?.clientId || +source?.sourceId < 0;
          const sources = (collection?.projectSources ?? []).concat([isOwned ? source : {
            sourceId: source.sourceId,
            type: source.type
          }]);
          if (source?.type === constants.sources.types.database) {
            sources[sources.length - 1].sourceId = (collection?.projectSources ?? []).reduce((m, s) => +s.sourceId < m ? +s.sourceId : m, 0) - 1;
          }
          const sourcesValue = utils.uniqueArray(sources, 'sourceId', true);

          return patchCollection(collection, {projectSources: sourcesValue});
        }

        if (action.details.type === constants.sources.types.suggestions) {
          return addSource({sourceId: 0, type: action.details.type});
        } else if (action.details.type === constants.sources.types.database) {
          return addSource({...action.details, sourceId: -1});
        } else {
          return createSource(action.details)
            .then((res) => {
              const newSource = res.response?.data?.data?.[0];
              return addSource({...action.details, sourceId: newSource?.params?.publicCollectionId});
            });
        }
      } else if (action.id === constants.assistant.adviceActionTypes.addField) {
        return new Promise((resolve, reject) => {
          const addField = () => {
            if (isUniverse) {
              return createClientField.mutation.mutateAsync({
                clientId: client?.clientId,
                ...action.details
              });
            } else {
              return createCollectionField.mutation.mutateAsync({
                clientId: client?.clientId,
                collectionId: collection?.collectionId,
                ...action.details
              });
            }
          };

          if (!hasFileUpload && action.details.autoLookup === false) {
            const handleConfirm = () => {
              const service = constants.data.lookup('services', constants.services.types.fileUploadEnrichment);

              return patchCollection(collection, {fileUploadPeriod: service?.period})
                .then(() => {
                  return addField().then(resolve);
                });
            }

            const handleClose = (e, reason) => {
              if (reason === 'cancelButtonClick') {
                addField().then(resolve);
              } else if (reason !== 'confirmButtonClick') {
                reject({abort: true});
              }
              dialogControl.hide();
            }

            dialogControl.show(<ConfirmDialog
              question={`You have selected to auto lookup in files. Do you wish to activate the files enrichment service${isUniverse ? ' for deal flow' : ''}?`}
              onConfirm={handleConfirm}
              CancelButtonProps={{
                children: 'No'
              }}
              ConfirmButtonProps={{
                children: 'Yes'
              }}/>, false, handleClose);
          } else if (!hasAutoLookup && action.details.autoLookup === true) {
            const handleConfirm = () => {
              const service = constants.data.lookup('services', constants.services.types.autoLookup);

              return patchCollection(collection, {autoLookupPeriod: service?.period})
                .then(() => {
                  addField().then(resolve);
                });
            }

            const handleClose = (e, reason) => {
              if (reason === 'cancelButtonClick') {
                addField().then(resolve);
              } else if (reason !== 'confirmButtonClick') {
                reject({abort: true});
              }
              dialogControl.hide();
            }

            dialogControl.show(<ConfirmDialog
              question={`You have selected to auto lookup online. Do you wish to activate the auto lookup service${isUniverse ? ' for deal flow' : ''}?`}
              onConfirm={handleConfirm}
              CancelButtonProps={{
                children: 'No'
              }}
              ConfirmButtonProps={{
                children: 'Yes'
              }}/>, false, handleClose);
          } else {
            addField()
              .then(resolve)
              .catch(reject);
          }
        });
      } else if ([
        constants.assistant.adviceActionTypes.addTagGroup,
        constants.assistant.adviceActionTypes.addTagGroupPoints,
        constants.assistant.adviceActionTypes.addTagGroupDescriptions,
        constants.assistant.adviceActionTypes.addTagGroupKeywords
      ].includes(action?.id)) {
        return new Promise((resolve, reject) => {
          const applyTagGroup = () => {
            if (action.id === constants.assistant.adviceActionTypes.addTagGroup) {
              const tagGroup = utils.toArray(action.details).map((tg) => {
                const tags = tg.tags.map((t) => {
                  return {
                    tagId: null,
                    value: t.value,
                    points: utils.isDefined(t.points) ? +t.points : null,
                    autoTagParams: t.autoTagParams
                  }
                });

                return {
                  ...tg,
                  tags: utils.underscoreEx(tags)
                };
              });

              return createTagGroup.mutation.mutateAsync({
                collectionId: collection?.collectionId,
                tagsGroup: tagGroup
              });
            } else {
              const oldTagGroup = collection?.tagGroups?.find((tg) => +tg.groupId === +action.details.groupId);
              const newTagGroup = utils.clone(oldTagGroup, true);

              if (action.id === constants.assistant.adviceActionTypes.addTagGroupPoints) {
                if (utils.isDefined(action.details.hasPoints)) {
                  newTagGroup.hasPoints = action.details.hasPoints;
                }
              } else if (action.id === constants.assistant.adviceActionTypes.addTagGroupDescriptions) {
                if (utils.isDefined(action.details.description)) {
                  newTagGroup.description = action.details.description;
                }
              }

              if (utils.isDefined(action.details.autoTagType)) {
                newTagGroup.autoTagType = action.details.autoTagType;
              }

              newTagGroup.tags.forEach((t) => {
                const nt = action.details.tags.find((ta) => +ta.tagId === +t.tagId);

                if (nt) {
                  if (action.id === constants.assistant.adviceActionTypes.addTagGroupPoints) {
                    t.points = +nt.points;
                  } else if (action.id === constants.assistant.adviceActionTypes.addTagGroupDescriptions) {
                    t.autoTagParams = t.autoTagParams || {};
                    t.autoTagParams.description = nt.autoTagParams.description;
                  } else if (action.id === constants.assistant.adviceActionTypes.addTagGroupKeywords) {
                    t.autoTagParams = t.autoTagParams || {};
                    t.autoTagParams.keywords = nt.autoTagParams.keywords ?? [];
                  }
                }
              });

              return updateTagGroup(collection, newTagGroup);
            }
          };

          if (!hasAutoTags && action.details.autoTagType !== constants.collection.autoTagTypes.noAutoAssign) {
            const handleConfirm = () => {
              const service = constants.data.lookup('services', constants.services.types.autotags);

              return patchCollection(collection, {autotagPeriod: service?.period})
                .then(() => {
                  applyTagGroup().then(resolve);
                });
            }

            const handleClose = (e, reason) => {
              if (reason === 'cancelButtonClick') {
                applyTagGroup().then(resolve);
              } else if (reason !== 'confirmButtonClick') {
                reject({abort: true});
              }
              dialogControl.hide();
            }

            dialogControl.show(<ConfirmDialog
              question={`You have selected to auto-assign tags. Do you wish to activate the auto tags service${isUniverse ? ' for deal flow' : ''}?`}
              onConfirm={handleConfirm}
              CancelButtonProps={{
                children: 'No'
              }}
              ConfirmButtonProps={{
                children: 'Yes'
              }}/>, false, handleClose);
          } else {
            applyTagGroup()
              .then(resolve)
              .catch(reject);
          }
        });
      } else if ([
        constants.assistant.adviceActionTypes.enableService,
        constants.assistant.adviceActionTypes.disableService
      ].includes(action?.id)) {
        const service = constants.data.lookup('services', action.details.type);

        return patchCollection(collection, {
          [service.periodField]: action.id === constants.assistant.adviceActionTypes.enableService ?
            service?.period : constants.services.periods.never
        });
      }
    };

    return applyAction()
      .then(() => {
        tableProvider.assistantState?.setAssistantActions((current) => ([
          ...(current ?? []),
          ...[{
            actionId: action.actionId,
            answer: answer,
            applied: true
          }]
        ]));
      });
  };

  return <StyledEntitiesAssistant ref={ref} {...innerProps}
                                  count={tableProvider?.list?.meta?.resultsCount}
                                  state={tableProvider.assistantState}
                                  types={types}
                                  isLoading={isLoading}
                                  onClear={handleClear}
                                  onSwitch={handleSwitch}
                                  onChange={handleChange}
                                  onAdvice={handleAdvice}
                                  onClose={handleClose}
                                  onAction={handleAction}>
    {innerProps.children}
  </StyledEntitiesAssistant>
});

EntitiesAssistant.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ])
};

export default EntitiesAssistant;
